import React, { useState, useEffect, createContext } from 'react';

// GraphQL Queries and Mutations
const productQuery = `
  query getProduct($id: ID!) {
    product(id: $id) {
      id
      title
      handle
      description
      variants(first: 250) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
            image {
              src
              altText
            }
          }
        }
      }
    }
  }
`;

// Updated - Cart Create Mutation (replacing checkoutCreate)
const cartCreateMutation = `
  mutation cartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 250) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    src
                    altText
                  }
                  product {
                    title
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

// Updated - Cart Fetch Query (replacing checkoutFetch)
const cartFetchQuery = `
  query cartFetch($id: ID!) {
    cart(id: $id) {
      id
      checkoutUrl
      lines(first: 250) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                price {
                  amount
                  currencyCode
                }
                image {
                  src
                  altText
                }
                product {
                  title
                }
              }
            }
            attributes {
              key
              value
            }
          }
        }
      }
      cost {
        totalAmount {
          amount
          currencyCode
        }
      }
    }
  }
`;

// Updated - Cart Lines Add Mutation (replacing checkoutLineItemsAdd)
const cartLinesAddMutation = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 250) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    src
                    altText
                  }
                  product {
                    title
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

// Updated - Cart Lines Update Mutation (replacing checkoutLineItemsUpdate)
const cartLinesUpdateMutation = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 250) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    src
                    altText
                  }
                  product {
                    title
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

// Updated - Cart Lines Remove Mutation (replacing checkoutLineItemsRemove)
const cartLinesRemoveMutation = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
        lines(first: 250) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    src
                    altText
                  }
                  product {
                    title
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

const defaultState = {
  cart: {},
};

const CartContext = createContext(defaultState);

export default CartContext;

export function CartContextProvider({ children }) {
  const [cart, setCart] = useState(
    JSON.parse(
      typeof window !== 'undefined' ? localStorage.getItem('ledandcollared_cart') : null
    )
  );
  
  const [successfulOrder, setSuccessfulOrder] = useState(null);
  const cartId = cart?.id;

  // Helper function to execute GraphQL queries with direct fetch
  async function executeQuery(query, variables) {
    try {
      // console.log('🔍 Executing GraphQL query with variables:', variables);
      
      // Updated - API endpoint from 2023-10 to 2024-07
      const response = await fetch('https://ledandcollared.myshopify.com/api/2024-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': process.env.GATSBY_SHOPIFY_STORE_FRONT_ACCESS_TOKEN
        },
        body: JSON.stringify({
          query,
          variables
        })
      });
      
      // console.log('🔄 Response status:', response.status);
      
      const responseData = await response.json();
      
      if (responseData.errors) {
        console.error('❌ GraphQL errors:', responseData.errors);
        throw responseData.errors;
      }
      
      // console.log('✅ Query successful');
      return responseData;
    } catch (error) {
      console.error('❌ Query execution error:', error);
      
      // Try to extract more detailed error information
      if (error.response) {
        try {
          const errorText = await error.response.text();
          console.error('🔍 Error response body:', errorText);
        } catch (e) {
          console.error('Could not extract error response text');
        }
      }
      
      throw error;
    }
  }

  useEffect(() => {
    const getCart = async () => {
      if (cartId && typeof window !== 'undefined') {
        // console.log('🔄 Fetching cart:', cartId);
        try {
          const responseData = await executeQuery(cartFetchQuery, { id: cartId });
          
          const fetchedCart = responseData.data.cart;
          
          if (fetchedCart) {
            // console.log('✅ Fetched existing cart');
            const transformedCart = transformCartData(fetchedCart);
            setCart(transformedCart);
            localStorage.setItem('ledandcollared_cart', JSON.stringify(transformedCart));
          } else {
            console.error('❌ Failed to fetch cart, it might be expired or invalid.');
            localStorage.removeItem('ledandcollared_cart');
            setCart(null);
            setSuccessfulOrder(null);
          }        
        } catch (error) {
          console.error('❌ Error fetching cart:', error);
          localStorage.removeItem('ledandcollared_cart');
          setCart(null);
          setSuccessfulOrder(null);
        }
      }
    };    
    getCart();
  }, [setCart, setSuccessfulOrder, cartId]);

  // Helper function to transform cart data structure to maintain backward compatibility
  function transformCartData(cart) {
    if (!cart || !cart.lines || !cart.lines.edges) {
      console.error('❌ Invalid cart data structure:', cart);
      return cart;
    }
    
    return {
      ...cart,
      // Map from cart structure to checkout-compatible structure for backward compatibility
      webUrl: cart.checkoutUrl, // For backward compatibility
      lineItems: cart.lines.edges.map(edge => {
        if (!edge.node.merchandise || !edge.node.merchandise.product) {
          console.error('❌ Invalid line item structure:', edge.node);
          return null;
        }
        
        return {
          id: edge.node.id,
          title: edge.node.merchandise.product.title,
          quantity: edge.node.quantity,
          variant: {
            id: edge.node.merchandise.id,
            title: edge.node.merchandise.title,
            priceV2: {
              amount: edge.node.merchandise.price.amount,
              currencyCode: edge.node.merchandise.price.currencyCode
            },
            image: edge.node.merchandise.image
          },
          customAttributes: edge.node.attributes
        };
      }).filter(item => item !== null),
      totalPriceV2: {
        amount: cart.cost.totalAmount.amount,
        currencyCode: cart.cost.totalAmount.currencyCode
      }
    };
  }

  async function getProductById(productId) {
    try {
      // console.log('🔍 Fetching product:', productId);

      const responseData = await executeQuery(productQuery, { id: productId });
      // console.log('✅ Product fetched successfully');
      
      // Access the data from the correct path in the response
      const product = responseData.data.product;
      
      if (!product) {
        console.error('❌ Product not found in response');
        return null;
      }

      // Transform the response
      const transformedProduct = {
        ...product,
        variants: product.variants.edges.map(edge => ({
          id: edge.node.id,
          title: edge.node.title,
          priceV2: {
            amount: edge.node.price.amount,
            currencyCode: edge.node.price.currencyCode
          },
          compareAtPriceV2: edge.node.compareAtPrice ? {
            amount: edge.node.compareAtPrice.amount,
            currencyCode: edge.node.compareAtPrice.currencyCode
          } : null,
          availableForSale: edge.node.availableForSale,
          selectedOptions: edge.node.selectedOptions,
          image: edge.node.image
        }))
      };
    
      return transformedProduct;

    } catch (error) {
      console.error('❌ Error fetching product:', error);
      
      let data = new FormData();
      data.append('type', 'getProductById(productId)');
      data.append('error', error.message || JSON.stringify(error));
      data.append('product', productId);
  
      fetch('/api/errors', {
        method: 'POST',
        body: data,
      });
      return null;
    }
  }

  // Update line item in cart (changing quantity)
  const updateCartLineItem = async ({ variantId, quantity }) => {
    // console.log('🔄 Updating cart item:', { variantId, quantity });
    try {
      if (!cart || !cart.id) {
        console.error('❌ No cart found to update');
        return;
      }
      
      const lineItem = cart.lineItems?.find(
        lineItem => lineItem.variant.id === variantId
      );
      
      if (!lineItem) {
        console.error('❌ Line item not found in cart');
        return;
      }
      
      const responseData = await executeQuery(cartLinesUpdateMutation, {
        cartId: cart.id,
        lines: [{
          id: lineItem.id,
          quantity: quantity,
        }]
      });
      
      // console.log('✅ Cart item updated successfully');
      const newCart = transformCartData(responseData.data.cartLinesUpdate.cart);
      
      setCart(newCart);
      setSuccessfulOrder(null);
      if (typeof window !== 'undefined') {
        localStorage.setItem('ledandcollared_cart', JSON.stringify(newCart));
      }
      setAddToCart(true);
      setTimeout(() => {
        addCart(false);
      }, 1000);
    } catch (error) {
      console.error('❌ Error updating cart item:', error);
    }
  };

  // Add new item to cart
  const updateLineItem = async ({ variantId, quantity, customSelections }) => {
    // console.log('🛒 Adding to cart:', { variantId, quantity });
    try {
      let newCart = cart;
      
      // If no cart exists, create one
      if (!newCart) {
        const responseData = await executeQuery(cartCreateMutation, {
          input: {}
        });
        
        // console.log('✅ Created new cart');
        newCart = transformCartData(responseData.data.cartCreate.cart);
      }
      
      // Format custom attributes
      const attributes = customSelections ? customSelections.map(({ key, value }) => ({ 
        key, 
        value: String(value) // Ensure value is a string
      })) : [];
      
      // Add the line item
      const responseData = await executeQuery(cartLinesAddMutation, {
        cartId: newCart.id,
        lines: [{
          merchandiseId: variantId,
          quantity,
          attributes
        }]
      });
      
      // console.log('✅ Item added to cart');
      const updatedCart = transformCartData(responseData.data.cartLinesAdd.cart);
      
      setCart(updatedCart);
      setSuccessfulOrder(null);
      if (typeof window !== 'undefined') {
        localStorage.setItem('ledandcollared_cart', JSON.stringify(updatedCart));
      }
      setAddToCart(true);
      setTimeout(() => {
        addCart(false);
      }, 1000);
    } catch (error) {
      console.error('❌ Error adding item to cart:', error);
    }
  };

  // Remove line item from cart
  const removeLineItem = async (lineItemId) => {
    // console.log('🗑️ Removing item from cart:', lineItemId);
    try {
      if (!cart || !cart.id) {
        console.error('❌ No cart found');
        return;
      }
      
      const responseData = await executeQuery(cartLinesRemoveMutation, {
        cartId: cart.id,
        lineIds: [lineItemId]
      });
      
      // console.log('✅ Item removed from cart');
      const newCart = transformCartData(responseData.data.cartLinesRemove.cart);
      
      setCart(newCart);
      if (typeof window !== 'undefined') {
        localStorage.setItem('ledandcollared_cart', JSON.stringify(newCart));
      }
    } catch (error) {
      console.error('❌ Error removing item from cart:', error);
    }
  };

  const dismissSuccessfulOrder = () => {
    setSuccessfulOrder(null);
  };

  const [addToCart, setAddToCart] = useState(false)
  const addCart = (e) => {
    setAddToCart(e);
  };

  const [cartOpen, setCartOpen] = useState(false)

  // For backward compatibility: provide both cart and checkout properties
  const checkout = cart; // Make checkout property reference the cart for compatibility

  return (
    <CartContext.Provider
      value={{
        updateCartLineItem,
        cartOpen,
        setCartOpen,
        checkout, // Keep the checkout reference for backward compatibility
        cart, // Add the new cart property
        updateLineItem,
        removeLineItem,
        getProductById,
        successfulOrder,
        dismissSuccessfulOrder,
        addToCart,
        addCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
}